/**
 * Lets one part of the page tell the rest to land its next change with nothing fading. A month
 * button jumps the page to a section that scrolls in a bar from outside it, so the bar has to stop
 * fading in the same frame as the section does.
 */
import * as stylex from "@stylexjs/stylex";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";

/** Worn for the frames after a jump so every transition below it lands at once. */
export const instant = {
  x17muy3b: "xh2wz5i x17muy3b",
  $$css: true
};
const JUMPED = "lattice-jumped";

/** Call before anything changes. Wearers have committed the zero timings when this returns. */
export function announceJump() {
  window.dispatchEvent(new Event(JUMPED));
}

/** True from an announced jump until two frames later, when its fades would have finished unseen. */
export function useJump(): boolean {
  const [jumping, setJumping] = useState(false);
  useEffect(() => {
    const start = () => {
      /* Committed before anything changes: a fade that has started is not shortened by a theme that
         arrives after it. */
      flushSync(() => setJumping(true));
      requestAnimationFrame(() => requestAnimationFrame(() => setJumping(false)));
    };
    window.addEventListener(JUMPED, start);
    return () => window.removeEventListener(JUMPED, start);
  }, []);
  return jumping;
}

/** The longest a change that never reports is waited for. */
const REPORT_TIMEOUT_MS = 400;

/** What `useJumpUntilReported` hands back. */
interface JumpUntilReported {
  readonly jumping: boolean;
  readonly report: () => void;
}

/**
 * Like `useJump`, for a part of the page that learns what to change from an observer. An
 * IntersectionObserver reports a few frames after the scroll, past the two frames `useJump` covers,
 * so the change would otherwise fade in on the first press. Stays true from an announced jump until
 * two frames after `report` is called, or until the timeout when the jump changes nothing.
 */
export function useJumpUntilReported(): JumpUntilReported {
  const [jumping, setJumping] = useState(false);
  const waiting = useRef(false);
  const timer = useRef(0);
  useEffect(() => {
    const stop = () => {
      waiting.current = false;
      window.clearTimeout(timer.current);
      setJumping(false);
    };
    const start = () => {
      flushSync(() => setJumping(true));
      waiting.current = true;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(stop, REPORT_TIMEOUT_MS);
    };
    window.addEventListener(JUMPED, start);
    return () => {
      window.removeEventListener(JUMPED, start);
      window.clearTimeout(timer.current);
    };
  }, []);
  const report = useCallback(() => {
    if (!waiting.current) return;
    waiting.current = false;
    window.clearTimeout(timer.current);
    requestAnimationFrame(() => requestAnimationFrame(() => setJumping(false)));
  }, []);
  return {
    jumping,
    report
  };
}